import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react'
import { Camera, Upload, FileText, CheckCircle2, X } from 'lucide-react'
import styles from './DocumentUpload.module.scss'

export default function DocumentUpload({ label, caption, value, onChange, accept = 'image/jpeg,image/png,image/webp,image/gif,image/heic,.heic,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document', error, required }) {
  useInterfaceLanguage();
  const uploadRef = useRef(null)
  const captureRef = useRef(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const mounted = useRef(true)
  const [preview, setPreview] = useState('')
  const [camera, setCamera] = useState(false)
  const [message, setMessage] = useState('')
  const [opening, setOpening] = useState(false)
  const [previewUnavailable, setPreviewUnavailable] = useState(false)
  const stop = () => { streamRef.current?.getTracks().forEach(track => track.stop()); streamRef.current = null }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; stop() } }, [])
  useEffect(() => {
    setPreviewUnavailable(false)
    if (!value) { setPreview(''); return }
    if (typeof value === 'string') { setPreview(/\.(?:pdf|docx?)(?:\?|$)/i.test(value) ? '' : value); return }
    if (!value.type.startsWith('image/')) { setPreview(''); return }
    const url = URL.createObjectURL(value)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [value])
  useEffect(() => { if (camera && videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current }, [camera])
  const choose = file => {
    if (!file) return
    if (file.size > 10 * 1024 * 1024) { setMessage(tr("Размер файла не должен превышать 10 МБ.")); return }
    const image = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic'].includes(file.type)
    const document = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)
    if (!image && !(document && accept.includes('application/pdf'))) { setMessage(tr("Выберите JPG, PNG, WebP, GIF, HEIC") + (accept.includes('application/pdf') ? tr(", PDF, DOC или DOCX.") : '.')); return }
    setMessage(''); onChange(file)
  }
  const openCamera = async () => {
    setMessage('')
    if (window.matchMedia('(pointer: coarse)').matches) { captureRef.current?.click(); return }
    if (!navigator.mediaDevices?.getUserMedia) { setMessage(tr("Камера недоступна. Загрузите готовую фотографию.")); return }
    setOpening(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      if (!mounted.current) { stream.getTracks().forEach(track => track.stop()); return }
      streamRef.current = stream; setCamera(true)
    } catch { if (mounted.current) setMessage(tr("Не удалось открыть камеру. Проверьте разрешение браузера или загрузите фотографию.")) }
    finally { if (mounted.current) setOpening(false) }
  }
  const takePhoto = () => {
    const video = videoRef.current
    if (!video?.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth; canvas.height = video.videoHeight
    canvas.getContext('2d').drawImage(video, 0, 0)
    canvas.toBlob(blob => {
      if (blob && mounted.current) { choose(new File([blob], 'photo-' + Date.now() + '.jpg', { type: 'image/jpeg' })); stop(); setCamera(false) }
    }, 'image/jpeg', .88)
  }
  const pick = event => { choose(event.target.files?.[0]); event.target.value = '' }
  const filename = typeof value === 'string' ? value.split('/').pop().split('?')[0] : value?.name
  return <section className={styles.upload} aria-label={tr(label)}>
    <div className={styles.title}><FileText size={16} /><strong>{tr(label)}{tr(required && <span> *</span>)}</strong>{tr(value && <CheckCircle2 className={styles.done} size={16} />)}</div>
    {tr(caption && <p className={styles.caption}>{tr(caption)}</p>)}
    {camera ? <div className={styles.camera}><video ref={videoRef} autoPlay playsInline muted /><div><button type="button" onClick={takePhoto}><Camera size={17} />{tr("Сделать снимок")}</button><button type="button" onClick={() => { stop(); setCamera(false) }}><X size={16} />{tr("Закрыть камеру")}</button></div></div>
      : <div className={styles.preview}>{preview && !previewUnavailable ? <img src={preview} alt={tr(label)} onError={() => setPreviewUnavailable(true)} /> : <div className={styles.placeholder}><FileText size={25} /><span>{value ? previewUnavailable ? tr("Файл выбран · предпросмотр недоступен") : tr("Документ выбран") : tr("Фото или документ")}</span></div>}</div>}
    {tr(value && <div className={styles.filename}><CheckCircle2 size={13} /><span>{tr(filename)}</span>{tr(typeof value === 'string' && <a href={value} target="_blank" rel="noreferrer">{tr("Открыть")}</a>)}</div>)}
    <div className={styles.actions}><button type="button" onClick={openCamera} disabled={opening || camera}><Camera size={16} />{opening ? tr("Открываем…") : tr("Сфотографировать")}</button><button type="button" onClick={() => uploadRef.current?.click()}><Upload size={16} />{value ? tr("Заменить файл") : tr("Загрузить")}</button></div>
    <input className={styles.hidden} ref={uploadRef} type="file" accept={accept} onChange={pick} tabIndex={-1} />
    <input className={styles.hidden} ref={captureRef} type="file" accept="image/*" capture="environment" onChange={pick} tabIndex={-1} />
    <p className={styles.hint}>{tr("JPG, PNG, WebP, GIF, HEIC")}{tr(accept.includes('application/pdf') && ', PDF, DOC, DOCX')}{tr(" · до 10 МБ")}</p>
    {tr((error || message) && <p className={styles.error} role="alert">{tr(error || message)}</p>)}
  </section>
}
